// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// "Critical Systems & Plant" band under the floor plan: the plant metric
// cards, the facility-vs-HVAC load comparison and the HVAC fault log.

import React from 'react';
import type { CriticalSystems, LoadBar, PlantMetricGroup } from '../../types/floorPlan';
import { SevTag } from '../shell/ScreenHelpers';
import { MissingNote, MissingValue } from '../common/MissingValue';
import {
  CRITICAL_SYSTEMS_UNPUBLISHED,
  HVAC_FAULTS_UNBOUND,
  LOAD_COMPARISON_UNPUBLISHED,
} from './missingData';
import {
  LOAD_COMPARISON_BARS,
  LOAD_COMPARISON_TITLE,
  PLANT_GROUPS,
  type PlantGroupSpec,
} from './plantReadouts';
import { useSiteTime } from '../../hooks/useSiteTime';

function MetricCard({ group }: { group: PlantMetricGroup }) {
  return (
    <section className="fp-card" aria-labelledby={`fp-card-${group.id}`}>
      <h3 className="fp-card-title" id={`fp-card-${group.id}`}>
        {group.title}
      </h3>
      <dl className="fp-metrics">
        {group.metrics.map(m => (
          <div className="fp-metric" key={m.label}>
            <dt className="fp-metric-label">{m.label}</dt>
            <dd className="fp-metric-value">
              {m.value}
              {m.unit && <span className="fp-metric-unit">{m.unit}</span>}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * Two-bar comparison drawn as plain SVG — the shape is fixed (facility total
 * vs the HVAC share of it), so a chart library would cost more than it saves.
 * Bars scale against the largest value rather than a fixed ceiling.
 */
function LoadChart({ title, unitLabel, bars }: CriticalSystems['load']) {
  const peak = Math.max(...bars.map(b => b.kw), 1);
  const ceiling = Math.ceil(peak / 2000) * 2000;
  const ticks = [ceiling, ceiling / 2];
  const plotHeight = 92;
  const barWidth = 26;
  const slot = 56;

  return (
    <section className="fp-card fp-card-chart" aria-labelledby="fp-card-load">
      <h3 className="fp-card-title" id="fp-card-load">
        {title}
      </h3>
      <div className="fp-chart">
        <ul className="fp-chart-axis" aria-hidden="true">
          {ticks.map(t => (
            <li key={t}>{t / 1000}k</li>
          ))}
          <li>0</li>
        </ul>
        <svg
          className="fp-chart-plot"
          viewBox={`0 0 ${slot * bars.length} ${plotHeight + 14}`}
          role="img"
          aria-label={bars.map(b => `${b.label} ${b.kw} ${unitLabel}`).join(', ')}
        >
          {ticks.map(t => (
            <line
              key={t}
              x1="0"
              x2={slot * bars.length}
              y1={plotHeight - (t / ceiling) * plotHeight}
              y2={plotHeight - (t / ceiling) * plotHeight}
              className="fp-chart-grid"
            />
          ))}
          {bars.map((b: LoadBar, i: number) => {
            const h = (b.kw / ceiling) * plotHeight;
            return (
              <g key={b.label}>
                <rect
                  x={i * slot + (slot - barWidth) / 2}
                  y={plotHeight - h}
                  width={barWidth}
                  height={h}
                  className={b.accent ? 'fp-chart-bar fp-chart-bar-accent' : 'fp-chart-bar'}
                />
                <text
                  x={i * slot + slot / 2}
                  y={plotHeight + 11}
                  textAnchor="middle"
                  className="fp-chart-label"
                >
                  {b.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </section>
  );
}

function FaultLog({ faults }: { faults: CriticalSystems['faults'] }) {
  const { format } = useSiteTime();
  return (
    <section className="fp-card fp-card-faults" aria-labelledby="fp-card-faults">
      <h3 className="fp-card-title" id="fp-card-faults">
        HVAC faults
      </h3>
      {faults.length === 0 ? (
        <p className="fp-faults-empty">No HVAC faults logged.</p>
      ) : (
        <table className="fp-faults">
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Time</th>
              <th scope="col">Sev</th>
              <th scope="col">Event</th>
            </tr>
          </thead>
          <tbody>
            {faults.map(f => (
              <tr key={f.id}>
                <td className="fp-faults-mono">{format(f.eventTimestamp, 'dateShort')}</td>
                <td className="fp-faults-mono">{format(f.eventTimestamp, 'clock')}</td>
                <td>
                  <SevTag sev={f.sev} />
                </td>
                <td>{f.event}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * A metric card with no readings behind it. Same card, same rows, same order —
 * only the values are replaced, so the band still shows which points the screen
 * expects and the layout does not shift when they arrive.
 */
function MissingMetricCard({ group }: { group: PlantGroupSpec }) {
  return (
    <section className="fp-card" aria-labelledby={`fp-card-${group.id}`}>
      <h3 className="fp-card-title" id={`fp-card-${group.id}`}>
        {group.title}
      </h3>
      <dl className="fp-metrics">
        {group.readouts.map(r => (
          <div className="fp-metric" key={r.key}>
            <dt className="fp-metric-label">{r.label}</dt>
            <dd className="fp-metric-value">
              <MissingValue reason={CRITICAL_SYSTEMS_UNPUBLISHED} />
              {r.unit && <span className="fp-metric-unit">{r.unit}</span>}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * The comparison with no bars. The plot area keeps its height so the card holds
 * its place in the grid; drawing zero-height bars would read as zero load.
 */
function MissingLoadChart() {
  return (
    <section className="fp-card fp-card-chart" aria-labelledby="fp-card-load">
      <h3 className="fp-card-title" id="fp-card-load">
        {LOAD_COMPARISON_TITLE}
      </h3>
      <div className="fp-chart fp-chart-missing">
        <ul className="fp-chart-axis" aria-hidden="true">
          <li>
            <MissingValue reason={LOAD_COMPARISON_UNPUBLISHED} />
          </li>
        </ul>
        <ul className="fp-chart-missing-bars">
          {LOAD_COMPARISON_BARS.map(b => (
            <li key={b.key}>
              <MissingValue reason={LOAD_COMPARISON_UNPUBLISHED} />
              <span className="fp-chart-label">{b.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** The fault log with no feed bound. Distinct from "no faults logged". */
function MissingFaultLog() {
  return (
    <section className="fp-card fp-card-faults" aria-labelledby="fp-card-faults">
      <h3 className="fp-card-title" id="fp-card-faults">
        HVAC faults
      </h3>
      <MissingNote>{HVAC_FAULTS_UNBOUND}</MissingNote>
    </section>
  );
}

/**
 * The band keeps every card in both states. `data` is null while no endpoint
 * publishes plant readouts: the cards, their rows and the comparison stay put
 * and each value position names the gap, rather than the section vanishing.
 */
export default function CriticalSystemsPanel({ data }: { data: CriticalSystems | null }) {
  return (
    <section className="fp-critical" aria-labelledby="fp-critical-heading">
      <h2 className="fp-critical-heading" id="fp-critical-heading">
        Critical Systems &amp; Plant
      </h2>
      <div className="fp-critical-grid">
        {data ? (
          <>
            {data.groups.map(g => (
              <MetricCard key={g.id} group={g} />
            ))}
            <LoadChart {...data.load} />
            <FaultLog faults={data.faults} />
          </>
        ) : (
          <>
            {PLANT_GROUPS.map(g => (
              <MissingMetricCard key={g.id} group={g} />
            ))}
            <MissingLoadChart />
            <MissingFaultLog />
          </>
        )}
      </div>
    </section>
  );
}
